import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { SERVICE_STATUS_LABELS, SERVICE_STATUS_COLORS } from "@/lib/api";
import { CarTaxiFront, CalendarClock, Phone } from "lucide-react";

export default function DriverDashboardPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pastDate, setPastDate] = useState("");
  const [past, setPast] = useState([]);
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/services", { params: { date_from: today } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pastDate) return setPast([]);
    api.get("/services", { params: { date_from: pastDate, date_to: pastDate } }).then((r) => setPast(r.data));
  }, [pastDate]);

  const todayItems = items.filter((s) => s.pickup_date === today);
  const upcoming = items.filter((s) => s.pickup_date > today);

  const Card = ({ s }) => {
    const color = SERVICE_STATUS_COLORS[s.status] || "#0A0A0A";
    return (
      <Link
        to={`/services/${s.id}`}
        className="block border border-black bg-white p-5 hover:bg-zinc-50 transition-colors"
        data-testid={`driver-service-${s.booking_number}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono font-bold text-lg">{s.booking_number}</div>
          <span
            className="status-pill"
            style={{ borderColor: color, color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {SERVICE_STATUS_LABELS[s.status]}
          </span>
        </div>
        <div className="font-display text-xl font-bold mb-2">{s.client_name}</div>
        <div className="text-sm text-zinc-600 space-y-1">
          <div className="flex items-center gap-2">
            <CalendarClock size={14} /> {s.pickup_date} · {s.pickup_time}
          </div>
          {s.client_phone && (
            <div className="flex items-center gap-2">
              <Phone size={14} /> {s.client_phone}
            </div>
          )}
        </div>
      </Link>
    );
  };

  const Section = ({ title, data }) => (
    <section>
      <h2 className="font-display text-xl font-bold uppercase tracking-wider mb-4 border-b border-black pb-2">
        {title}{" "}
        <span className="text-zinc-400 text-sm font-normal ml-2">({data.length})</span>
      </h2>
      {data.length === 0 ? (
        <div className="text-sm text-zinc-500 mb-6">Sin servicios.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
          {data.map((s) => (
            <Card key={s.id} s={s} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px] mx-auto" data-testid="driver-dashboard">
      <header className="mb-6 sm:mb-8 flex items-center gap-3">
        <CarTaxiFront size={28} className="sm:w-8 sm:h-8" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Conductor
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
            Mis servicios
          </h1>
        </div>
      </header>

      {loading ? (
        <div className="text-center text-zinc-500 py-12">Cargando…</div>
      ) : (
        <>
          <Section title="Hoy" data={todayItems} />
          <Section title="Próximos" data={upcoming} />
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-sm font-semibold">Historial · elige un día:</span>
            <input
              type="date"
              max={today}
              value={pastDate}
              onChange={(e) => setPastDate(e.target.value)}
              className="border border-black h-10 px-3 text-sm"
              data-testid="driver-history-date"
            />
          </div>
          {pastDate && <Section title={`Historial ${pastDate}`} data={past} />}
        </>
      )}
    </div>
  );
}
